import { useEffect, useEffectEvent, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cx } from '../lib/cx'
import { Button } from './Button'
import { Textarea } from './Field'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg' | 'xl'
  /** Empêche la fermeture par Échap / clic extérieur (ex. pendant un envoi). */
  dismissible?: boolean
  className?: string
}

/**
 * Modale accessible : rendue dans un portail, `role="dialog"`, focus piégé,
 * Échap pour fermer, focus restauré sur l'élément d'origine à la fermeture,
 * défilement de la page bloqué.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  const requestClose = useEffectEvent(() => onClose())

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    const dialog = dialogRef.current
    const first =
      dialog?.querySelector<HTMLElement>('[data-autofocus]') ??
      dialog?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? dialog)?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && dismissible) {
        event.stopPropagation()
        requestClose()
        return
      }
      if (event.key !== 'Tab' || !dialog) return
      const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const firstItem = items[0]!
      const lastItem = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      previouslyFocused?.focus?.()
    }
  }, [open, dismissible])

  if (!open) return null

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx('modal', size !== 'md' && `modal-${size}`, className)}
      >
        <div className="modal-hd">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && (
              <p id={descId} className="text-muted" style={{ marginBlockStart: 'var(--space-1)' }}>
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon={<X />}
              aria-label="Fermer"
              onClick={onClose}
            />
          )}
        </div>
        {children && <div className="modal-body">{children}</div>}
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  )
}

export interface ConfirmModalProps {
  open: boolean
  onClose: () => void
  /** Reçoit le motif saisi quand `reasonRequired` est actif. */
  onConfirm: (reason?: string) => void | Promise<void>
  title: ReactNode
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'primary' | 'danger'
  /**
   * Motif obligatoire (cahier : suspension, prolongation, décision négative,
   * correction…). Le bouton reste désactivé tant que le motif est trop court.
   */
  reasonRequired?: boolean
  reasonLabel?: string
  reasonMinLength?: number
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  tone = 'primary',
  reasonRequired = false,
  reasonLabel = 'Motif',
  reasonMinLength = 10,
}: ConfirmModalProps) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  // Réinitialise le motif à chaque ouverture (état dérivé d'une prop, sans effet).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setReason('')
  }

  const reasonOk = !reasonRequired || reason.trim().length >= reasonMinLength

  async function handleConfirm() {
    if (!reasonOk) return
    setBusy(true)
    try {
      await onConfirm(reasonRequired ? reason.trim() : undefined)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={handleConfirm} loading={busy} disabled={!reasonOk}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        {message && <div>{message}</div>}
        {reasonRequired && (
          <Textarea
            data-autofocus
            label={reasonLabel}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint={`${reasonMinLength} caractères minimum. Le motif est conservé dans l’historique.`}
          />
        )}
      </div>
    </Modal>
  )
}

export interface DocumentPreviewModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** Contenu du document (PDF généré côté serveur, pièce téléchargée…). */
  blob: Blob | null
  fileName?: string
  /** Chargement impossible : message affiché à la place de l'aperçu. */
  error?: ReactNode
  /** Actions métier ajoutées au pied (ex. accepter / refuser une pièce). */
  actions?: ReactNode
  /** Contenu affiché sous l'aperçu (ex. formulaire de décision). */
  children?: ReactNode
}

/**
 * Aperçu plein cadre d'un document via une URL `blob:` locale, révoquée à la
 * fermeture — même rendu que le téléchargement, aucune URL publique (DESIGN.md).
 * Les images s'affichent en `<img>`, les PDF dans un cadre.
 */
export function DocumentPreviewModal({
  open,
  onClose,
  title,
  description,
  blob,
  fileName,
  error,
  actions,
  children,
}: DocumentPreviewModalProps) {
  const url = useMemo(() => (open && blob ? URL.createObjectURL(blob) : null), [open, blob])
  const label = typeof title === 'string' ? title : 'Aperçu du document'

  // Révoque l'URL blob dès qu'elle n'est plus affichée (fermeture, autre document).
  useEffect(() => {
    if (!url) return
    return () => URL.revokeObjectURL(url)
  }, [url])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="xl"
      footer={
        (url || actions) && (
          <>
            {url && (
              <a className="btn btn-secondary" href={url} download={fileName ?? 'document.pdf'}>
                Télécharger
              </a>
            )}
            {actions}
          </>
        )
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : url && blob?.type.startsWith('image/') ? (
          <div className="doc-image">
            <img src={url} alt={label} />
          </div>
        ) : url ? (
          <iframe className="doc-frame" src={url} title={label} />
        ) : (
          <span className="skeleton" style={{ height: '60vh' }} aria-busy="true" />
        )}
        {children}
      </div>
    </Modal>
  )
}
